import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.scss';
import './index.css';
import { RouterProvider } from 'react-router-dom';
import { router } from './router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { getUserLanguage } from './utils/langs';
import 'react-lazy-load-image-component/src/effects/opacity.css';
import { Helmet } from 'react-helmet';
import i18n from './localisation/i18n';
import * as Sentry from '@sentry/react';
import {
  backupCdnUrl,
  backupUrl,
  primaryCdnUrl,
  primaryUrl,
} from './constants';
import { ErrorFallback } from './widgets/ErrorFallback';
import { isInAppBrowser } from './utils/inAppBrowser';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24,
      retry: (failureCount, error) => {
        const axiosError = error as AxiosError;

        // 401 — не авторизован. Редиректим на /auth, но только если ещё
        // не там, иначе получим цикл перезагрузок. Точная проверка:
        // ровно '/auth' или вложенный роут '/auth/...'. Голый startsWith
        // ловил бы ложные совпадения (/author, /authority, /authorize).
        if (axiosError.response?.status === 401) {
          const path = window.location.pathname;
          if (path !== '/auth' && !path.startsWith('/auth/')) {
            window.location.href = '/auth';
          }
          return false; // Отключаем повторный запрос для 401
        }
        return failureCount < 3; // Для остальных ошибок допускаем 3 попытки
      },
    },
  },
});

axios.interceptors.request.use(
  config => {
    const language = getUserLanguage();

    // Axios v1 использует AxiosHeaders — лучше через set(), но падать не будем:
    if (config.headers && typeof config.headers.set === 'function') {
      config.headers.set('Accept-Language', language);
      config.headers.set('Cache-Control', 'no-store');
    } else {
      config.headers = config.headers || {};
      config.headers['Accept-Language'] = language;
      config.headers['Cache-Control'] = 'no-store';
    }

    return config;
  },
  error => {
    // сюда попадём, если сломалась подготовка запроса, а не HTTP-ответ
    return Promise.reject(error);
  }
);

// 2) РЕТРАЙ И ПЕРЕКЛЮЧЕНИЕ URL В RESPONSE-INTERCEPTOR
axios.interceptors.response.use(
  response => response,
  async error => {
    // Отменённый запрос (react-query снимает неактуальные через
    // AbortController) — штатная ситуация: не ретраим и не репортим.
    // Без этой проверки CanceledError без response выглядит как сетевой
    // сбой (status undefined) и уходит в 3 ретрая + Sentry error.
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const config = error.config;
    if (!config) {
      return Promise.reject(error);
    }

    // счётчик попыток
    config.__retryCount = config.__retryCount || 0;

    // Ретраим только то, что имеет смысл повторять: сетевые сбои (ответа
    // нет — status undefined), 5xx и 408/429. Остальные 4xx — клиентские
    // ошибки: повтор отправит то же тело и получит тот же ответ (например,
    // 400 «token is required» или 401 без логина), только удваивая нагрузку.
    const status: number | undefined = error.response?.status;
    const retryable =
      status === undefined || status >= 500 || status === 408 || status === 429;

    if (!retryable || config.__retryCount >= 3) {
      // лимит попыток исчерпан (или ретрай бессмысленен) — репортим.
      // 401 не репортим вовсе: «не авторизован» — нормальное состояние,
      // react-query-слой уже редиректит на /auth. 4xx — warning (доменное
      // состояние, которое вызывающий код обрабатывает), 5xx/сеть — error.
      if (status !== 401) {
        Sentry.captureException(error, {
          level: status !== undefined && status < 500 ? 'warning' : 'error',
        });
      }
      return Promise.reject(error);
    }

    config.__retryCount += 1;

    // чередуем хосты: 1-я/3-я попытки — backup, 2-я — обратно primary
    const useBackup = config.__retryCount % 2 === 1;

    // если у вас baseURL + относительные пути — переключайте baseURL:
    config.baseURL = useBackup ? backupUrl : primaryUrl;

    // если где-то формируете абсолютные URL — подменим и их:
    const swap = (u: any, from: any, to: any) =>
      u && u.includes(from) ? u.replace(from, to) : u;
    if (config.url) {
      // API-хост
      config.url = swap(
        config.url,
        primaryUrl,
        useBackup ? backupUrl : primaryUrl
      );
      config.url = swap(
        config.url,
        backupUrl,
        useBackup ? backupUrl : primaryUrl
      );
      // CDN-хост
      config.url = swap(
        config.url,
        primaryCdnUrl,
        useBackup ? backupCdnUrl : primaryCdnUrl
      );
      config.url = swap(
        config.url,
        backupCdnUrl,
        useBackup ? backupCdnUrl : primaryCdnUrl
      );
    }

    // необязательная задержка, чтобы не долбить сервер
    await new Promise(r => setTimeout(r, 300));

    return axios.request(config);
  }
);

axios.defaults.withCredentials = true;

// Телеметрия — только из сборки. Локальная разработка (vite dev,
// localhost:5173) не должна слать события в прод-Sentry: дев-ошибки
// перемешиваются с боевыми, а регистрации ServiceWorker на http- origin
// падают SecurityError-шумом (#1648/#1649).
if (!import.meta.env.DEV) {
  Sentry.init({
    dsn: 'https://9a9eec4b6ba24357b29eca9a1ebfb96f@gx10-7e45.tail5a1858.ts.net:8443/6',
    // Отсекаем ошибки сторонних скриптов, которые мы не чиним и не контролируем.
    // Пример: wrsParams (виджет/расширение браузера) регистрирует свой
    // service worker и падает с «Error: Rejected» — это не наш код.
    beforeSend: event => {
      const frames =
        event.exception?.values?.flatMap(v => v.stacktrace?.frames ?? []) ?? [];
      const hasExternalFrame = frames.some(
        frame =>
          typeof frame.function === 'string' &&
          frame.function.includes('wrsParams')
      );
      return hasExternalFrame ? null : event;
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* ErrorBoundary превращает белый экран при ошибке рендера в
          видимое сообщение + шлёт стек в Sentry. showDialog отключён,
          чтобы не мешать пользователям. */}
      <Sentry.ErrorBoundary
        fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}
      >
        <Helmet>
          <title>{i18n.t('title')}</title>
          <meta name="description" content={i18n.t('description')} />
        </Helmet>
        <RouterProvider router={router} />
      </Sentry.ErrorBoundary>
    </QueryClientProvider>
  </StrictMode>
);

/**
 * Регистрация Service Worker.
 *
 * Service Worker в in-app браузерах мессенджеров на iOS (WKWebView)
 * работает нестабильно: при первом визите по глубокой ссылке (?client=...)
 * SW с app-shell-стратегией (NavigationRoute → index.html) может перехватить
 * навигацию до готовности precache и отдать пустой/битый ответ → белый экран.
 *
 * Поэтому пропускаем регистрацию внутри in-app webview (там же вычищаем
 * ранее зарегистрированный SW, если он остался). В обычном браузере
 * регистрируем SW как обычно; при ошибке обновления нового воркера НЕ
 * перезагружаем страницу автоматически — переход нового воркера в состояние
 * `redundant` не мешает работать текущему (активному) воркеру, а автоматический
 * reload при битом деплое загнал бы всех пользователей в бесконечный цикл.
 * Случай «белого экрана после релиза» надёжно покрывается guard-скриптом
 * в index.html (таймаут 8с + одна попытка через sessionStorage).
 */
// В vite dev (localhost) sw.js не отдаётся — регистрация всегда падает
// SecurityError (MIME text/html), см. #1648/#1649. Не регистрируем вовсе.
if ('serviceWorker' in navigator && !isInAppBrowser() && !import.meta.env.DEV) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(err => {
      // Регистрация не удалась — это некритично (app продолжит работать
      // онлайн; обычно это разовый сетевой сбой при первом визите).
      // Уровень warning: пользовательского бага нет, ошибка сама не
      // повторится после следующей загрузки.
      Sentry.captureException(err, { level: 'warning' });
    });
  });
} else if ('serviceWorker' in navigator && isInAppBrowser()) {
  // В in-app webview гарантированно избавляемся от возможного ранее
  // зарегистрированного SW, чтобы он не ломал навигацию по ссылке.
  // .catch нужен: в приватных/restricted WebView getRegistrations()
  // может отклонить promise.
  navigator.serviceWorker
    .getRegistrations()
    .then(registrations => {
      registrations.forEach(reg => reg.unregister());
    })
    .catch(() => {});
}
